import { Grid } from '@mui/material';
import { Box } from '@mui/system';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { FormProvider, useForm, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IJobBoard, IJobBoardSaveDTO, saveJobBoard } from 'wisestep-api';
import {
  ArrowButton,
  WsTypography,
  WsTypoVariants,
  ControlledTextInput,
  WsButton,
} from 'wisestep-base';

import { useSKUModalStore } from '../../../../components/skuModal/SKUModalStore';
import { publishEmailValues } from '../../../../features/jobs/publish/components/helper/jobBoardHelper';
import {
  useJobBoardAccessControl,
  PremiumJobBoardsAction,
} from '../../../../hooks/useJobBoardAccessControl';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { JobBoardDialogHeader } from '../freeJobBoardSetting/FreeJobBoardSetting';
import {
  jobBoardSave,
  sharedSuccess,
  sharedFailed,
  deleteSharedPostings,
} from '../helper/jobBoardSettingsHelper';
import useJobBoardSettings from '../hooks/useJobBoardSettings';

import DeleteAccount from './DeleteAccount';
import SharedUserList, { BoldLabel } from './SharedUserList';
import ShareQuota from './ShareQuota';

type PropTypes = {
  jobBoardName: string;
  selectedAccount: IJobBoard;
  callBackHandler: (data: IJobBoard) => void;
};

const AccountSettings = ({ jobBoardName, selectedAccount, callBackHandler }: PropTypes) => {
  const navigate = useNavigate();
  const today: Date = new Date();
  const setModal = useSKUModalStore((state) => state.setModal);
  const [openDeleteAccountPopup, setOpenDeleteAccountPopup] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { access, owns } = useJobBoardAccessControl([
    PremiumJobBoardsAction.MANAGE_PREMIUM_JOB_POSTING_CREDITS,
    PremiumJobBoardsAction.DELETE_PREMIUM_JOB_POSTING_ACCOUNTS,
  ]);
  const [manage, deleteJobBoard] = access;
  const closeDeleteAccountPopup = () => {
    setOpenDeleteAccountPopup(false);
    navigate(`/job-boards/${jobBoardName}`);
  };

  const jobBoardResponse = useJobBoardSettings();
  jobBoardResponse.CareerBuilder;
  const methods = useForm();

  const saveApiCall = async (data: IJobBoardSaveDTO, type: string) => {
    try {
      await saveJobBoard(data);
      type == 'save' ? setNotification(sharedSuccess()) : setNotification(deleteSharedPostings());
      jobBoardName != 'CWJobs'
        ? navigate(`/job-boards/${jobBoardName}`)
        : navigate(`/job-boards/cwJobs-jobposting`);
    } catch (error: any) {
      error.response.status == 402
        ? setModal({ object: error.response.data, type: 'count' })
        : setNotification(sharedFailed());
    }
  };

  const sharedUserDeleteCallBack = async (data: IJobBoard) => {
    const updatedJobBoardSave = { ...jobBoardSave };
    updatedJobBoardSave.jobBoard = data;
    await saveApiCall(updatedJobBoardSave, 'delete');
    callBackHandler(data);
  };

  const onSaveEmail = async (data: FieldValues): Promise<boolean> => {
    const updatedJobBoardSave = { ...jobBoardSave };
    publishEmailValues.to = !isEmpty(data.to) ? [data.to] : [];
    publishEmailValues.subject = `Regarding ${jobBoardName} job posting`;
    updatedJobBoardSave.jobBoard = selectedAccount;
    updatedJobBoardSave.jobBoard.jobBoardSharedUsers = selectedAccount.jobBoardSharedUsers ?? [];
    if (data.totalPostingsAllowed)
      updatedJobBoardSave.jobBoard.totalPostingsAllowed = data.totalPostingsAllowed;
    if (
      updatedJobBoardSave.jobBoard.totalPostingsAllowed &&
      (selectedAccount.jobBoardSharedUsers?.reduce(
        (acc, item) => acc + item.maxPostingsPerMonth,
        0
      ) ?? 0) +
        parseInt(data.maxPostingsPerMonth) >
        updatedJobBoardSave.jobBoard.totalPostingsAllowed
    ) {
      setNotification(failed('Shared Credits Cannot Be More Than Total Credits '));
      return false;
    }
    if (!isEmpty(data.to) && data.maxPostingsPerMonth)
      updatedJobBoardSave.jobBoard.jobBoardSharedUsers.push({
        email: `${data.to}`,
        maxPostingsPerMonth: data.maxPostingsPerMonth,
        sharedDate: today,
        expiredDate: today,
      });
    try {
      await saveApiCall(updatedJobBoardSave, 'save');
      setNotification(success('Credits updated !'));
    } catch (e) {
      setNotification(failed('Failed to Update Credits !'));
    }
    return true;
  };

  return (
    <FormProvider {...methods}>
      <Box bgcolor="white">
        <JobBoardDialogHeader>
          <Grid item xs={12} container justifyContent="flex-start">
            <ArrowButton title="" onClick={() => navigate('/job-boards/')} />
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {`${jobBoardName} account settings`}
            </WsTypography>
          </Grid>
        </JobBoardDialogHeader>
        <Grid container p={3} display="flex" spacing={2}>
          <Grid item xs={9.5} alignSelf="start">
            <BoldLabel sx={{ mb: 0.5 }}>Account *</BoldLabel>
            <ControlledTextInput
              name="account"
              type="text"
              placeholder=""
              value={selectedAccount.username}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={2.5} pb={0.6} alignSelf="end">
            {(deleteJobBoard || owns) && (
              <WsButton
                variant="outlined"
                size="large"
                onClick={() => setOpenDeleteAccountPopup(true)}
              >
                Delete Account
              </WsButton>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} alignSelf="flex-end">
          {(owns || manage) && (
            <ShareQuota
              onSaveEmail={onSaveEmail}
              jobBoardName={jobBoardName}
              selectedAccount={selectedAccount}
            />
          )}
        </Grid>
        <Grid item xs={12} alignSelf="flex-end" pt={2}>
          <SharedUserList
            jobBoardName={jobBoardName}
            selectedAccount={selectedAccount}
            callBackHandler={sharedUserDeleteCallBack}
          />
        </Grid>
        {selectedAccount?.id && (
          <DeleteAccount
            selectedAccountName={selectedAccount?.username ?? ''}
            selectedAccountId={selectedAccount?.id}
            selectedAccountPassword={selectedAccount?.password}
            openDeleteAccountPopup={openDeleteAccountPopup}
            closeDeleteAccountPopup={closeDeleteAccountPopup}
          />
        )}
      </Box>
    </FormProvider>
  );
};

export default AccountSettings;
